import {
    computed,
    defineComponent,
    InjectionKey,
    onMounted,
    provide,
    reactive,
    ref,
    toRef,
} from '@vue/composition-api';
import { SpecificPropsType } from '@/components/form/types';
import {
    ConfTableProps,
    FetchOptions,
    inheritTableProps,
    Query,
} from '@/components/table/types';
import { Pagination, Table, TableColumn } from '@ks/ks-element-ui';
import { pick } from '@/components/form/utils/pick';
import style from './style.module.styl';

export const ConfTableKey: InjectionKey<{
    fetch(options?: FetchOptions): void;
}> = Symbol();

const offsetPage = (currentPage: number, startPage: number) =>
    currentPage - (1 - startPage);

export const ConfTable = defineComponent({
    props: {
        api: {},
        columns: {},
        pagination: {},
        fetchOnMounted: {},
        ...pick((Table as any).props, inheritTableProps),
    } as SpecificPropsType<ConfTableProps>,
    setup(props) {
        const loading = ref(false);
        const tableProps = computed(() => pick(props, inheritTableProps));
        const tableData = ref<object[]>([]);
        const pageData = props.pagination
            ? reactive({
                  currentPage: props.pagination.currentPage ?? 1,
                  total: props.pagination.total ?? 0,
                  pageSize: props.pagination.pageSize ?? 20,
              })
            : {
                  currentPage: 1,
                  total: 0,
                  pageSize: 20,
              };
        const startPage = computed(() => props.pagination?.startPage ?? 1);
        const generateQuery = ({ resetPage }: FetchOptions): Query => ({
            page: {
                currentPage: resetPage ? startPage.value : pageData.currentPage,
                pageSize: pageData.pageSize,
            },
        });

        const fetch = ({ resetPage }: FetchOptions = { resetPage: false }) => {
            if (props.api) {
                loading.value = true;
                const query = generateQuery({ resetPage });
                Promise.resolve(props.api(query))
                    .then(
                        (res) => {
                            if (Array.isArray(res)) {
                                tableData.value = res;
                                pageData.currentPage = query.page.currentPage;
                                return;
                            }
                            if (typeof res === 'object') {
                                tableData.value = res.data;
                                pageData.currentPage = query.page.currentPage;
                                pageData.total = res.total;
                            }
                        },
                        () => {},
                    )
                    .finally(() => {
                        loading.value = false;
                    });
            }
        };
        if (props.fetchOnMounted !== false) {
            onMounted(fetch);
        }

        provide(ConfTableKey, {
            fetch,
        });

        return {
            tableProps,
            tableData,
            fetch,
            loading,
            pageData,
        };
    },
    render() {
        const data = [...(this.$props?.data ?? []), ...this.tableData];
        const { startPage, ...paginationConfig } = this.pagination || {};

        return (
            <div v-loading={this.loading}>
                <Table
                    class={style.table}
                    {...{
                        props: {
                            ...this.tableProps,
                            data,
                        },
                    }}
                >
                    {this.columns.map((it) => {
                        const { label, render, renderHeader, ...rest } = it;
                        return (
                            <TableColumn
                                key={it.label}
                                {...{ props: { ...rest, label } }}
                                scopedSlots={{
                                    default: render,
                                    header: renderHeader,
                                }}
                            />
                        );
                    })}
                </Table>
                <Pagination
                    class={style.pagination}
                    hideOnSinglePage
                    layout="total, sizes, prev, pager, next, jumper"
                    onCurrent-change={(page: number) => {
                        this.pageData.currentPage = page;
                        this.fetch();
                    }}
                    onSize-change={(size: number) => {
                        this.pageData.pageSize = size;
                        this.pageData.currentPage = 1;
                        this.fetch();
                    }}
                    {...{
                        props: { ...this.pageData, ...paginationConfig },
                    }}
                />
            </div>
        );
    },
});
